import { useState, useEffect } from "react";

const useModifierModal = ({
  inputs,
  query = () => {},
  mutation = () => [undefined, {}],
  postMutation = () => [undefined, {}],
}) => {
  const [isOpen, setIsOpen] = useState();
  const [id, setId] = useState();
  const { data, status } = query(id);
  const [handleMutation, { status: mutationStatus }] = mutation();
  const [handlePostMutation] = postMutation();

  const isStrictNumber = (v) => {
    if (typeof v === "number") return Number.isFinite(v);
    if (typeof v !== "string") return false;
    if (v.trim() === "") return false;
    return Number.isFinite(Number(v));
  };

  const resetInputs = () => {
    if (!Array.isArray(inputs)) return;

    inputs.forEach((item) => {
      const field = item?.key;
      const control = item?.input;

      // Not ready / not for this record -> reset
      if (!id || !data || data?.id !== id || !field || !control) {
        control?.reset?.();
        return;
      }

      const raw = data?.[field];

      // If API returned nothing for this field -> reset
      if (raw == null) {
        control?.reset?.();
        return;
      }

      const opts = control?.options;
      const isDropdown = Array.isArray(opts);

      // Dropdown: set the matching option object (or fallback to {id})
      if (isDropdown) {
        const match = opts.find((o) => o?.id === raw);
        control?.setValue?.(match ?? { id: String(raw) });
        return;
      }

      // Numeric (for non-dropdown inputs): keep your rounding behavior
      if (isStrictNumber(raw)) {
        const n = Number(raw);
        control?.setValue?.(Math.round(n * 100) / 100);
        return;
      }

      // Plain input (string/object): set a primitive value
      // If backend returns an object, prefer its id (common pattern)
      if (typeof raw === "object") {
        control?.setValue?.(raw?.id ?? "");
        return;
      }

      // Text input: set the string as-is
      control?.setValue?.(String(raw));
    });
  };

  useEffect(() => {
    if (status === "fulfilled") {
      if (id != null) {
        resetInputs();
      }
    }
    // eslint-disable-next-line
  }, [status]);

  useEffect(() => {
    if (mutationStatus === "fulfilled") {
      if (id != null) {
        setId();
      }
    }
    // eslint-disable-next-line
  }, [mutationStatus]);

  useEffect(() => {
    resetInputs();
    // eslint-disable-next-line
  }, [id]);

  const onOpen = (id) => {
    setId(id);
    setIsOpen(true);
  };
  const onSubmit = () => {
    const params = { id };
    let body = {};
    if (Array.isArray(inputs)) {
      inputs?.map((input) => {
        if (input.input?.value != null && input.key) {
          body[input.key] =
            typeof input.input?.value !== "object"
              ? input.input?.value
              : input.input?.value?.id;
        }
        return false;
      });
    }
    if (id != null) {
      handleMutation(params, body);
    } else {
      handlePostMutation(null, body);
    }
  };
  const onCancel = () => {
    setIsOpen(false);
    setId();
  };
  const onClose = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    onOpen,
    onSubmit,
    onCancel,
    onClose,
    id,
  };
};

export default useModifierModal;
